import { c as _c } from "react/compiler-runtime";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatOrdinal } from "@/helpers/format.helpers";
import { faTrophy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FC } from "react";
import WinnerDropBadge from "../drops/winner/WinnerDropBadge";
interface SingleWaveDropPositionProps {
  readonly rank: number | null;
  readonly drop?: ExtendedDrop | undefined;
  readonly isFinalWinner?: boolean | undefined;
  readonly variant?: "default" | "simple" | undefined;
  readonly size?: "sm" | "md" | undefined;
}
const TrophyOnlyBadge: FC<{
  rank: number;
}> = t0 => {
  const $ = _c(7);
  const {
    rank
  } = t0;
  let accentColor;
  let bgColor;
  bb0: switch (rank) {
    case 1:
      {
        accentColor = "#fbbf24";
        bgColor = "rgba(251,191,36,0.1)";
        break bb0;
      }
    case 2:
      {
        accentColor = "#CAD5E3";
        bgColor = "rgba(202, 213, 227,0.1)";
        break bb0;
      }
    case 3:
      {
        accentColor = "#CD7F32";
        bgColor = "rgba(205,127,50,0.1)";
        break bb0;
      }
    default:
      {
        accentColor = "#848490";
        bgColor = "rgba(96,96,108,0.1)";
      }
  }
  const t1 = `1px solid ${accentColor}30`;
  let t2;
  if ($[0] !== accentColor || $[1] !== bgColor || $[2] !== t1) {
    t2 = {
      backgroundColor: bgColor,
      color: accentColor,
      border: t1
    };
    $[0] = accentColor;
    $[1] = bgColor;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="tw-px-2 tw-py-1 tw-text-xs tw-flex tw-items-center"><FontAwesomeIcon icon={faTrophy} className="tw-size-2.5 tw-flex-shrink-0" /></span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <div className="tw-flex tw-items-center tw-rounded-md tw-font-medium tw-whitespace-nowrap -tw-mt-0.5" style={t2}>{t3}</div>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const SimpleRankDisplay: FC<{
  rank: number;
  size?: "sm" | "md";
}> = t0 => {
  const $ = _c(11);
  const {
    rank,
    size: t1
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  let color;
  bb0: switch (rank) {
    case 1:
      {
        color = "tw-text-amber-400";
        break bb0;
      }
    case 2:
      {
        color = "tw-text-iron-300";
        break bb0;
      }
    case 3:
      {
        color = "tw-text-amber-600";
        break bb0;
      }
    default:
      {
        color = "tw-text-iron-500";
      }
  }
  const iconSize = size === "md" ? "tw-w-4 tw-h-4" : "tw-w-3.5 tw-h-3.5";
  const textSize = size === "md" ? "tw-text-md" : "tw-text-sm";
  const t2 = `tw-flex tw-items-center tw-gap-1.5 ${color}`;
  let t3;
  if ($[0] !== iconSize) {
    t3 = <FontAwesomeIcon icon={faTrophy} className={iconSize} />;
    $[0] = iconSize;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `${textSize} tw-font-semibold`;
  let t5;
  if ($[2] !== rank) {
    t5 = formatOrdinal(rank);
    $[2] = rank;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t6) {
    t7 = <div className={t2}>{t3}{t6}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
export const SingleWaveDropPosition: FC<SingleWaveDropPositionProps> = t0 => {
  const $ = _c(7);
  const {
    rank,
    drop,
    isFinalWinner: t1,
    variant: t2,
    size: t3
  } = t0;
  const isFinalWinner = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const size = t3 === undefined ? "sm" : t3;
  if (!rank) {
    return null;
  }
  if (variant === "simple") {
    let t4;
    if ($[0] !== rank || $[1] !== size) {
      t4 = <SimpleRankDisplay rank={rank} size={size} />;
      $[0] = rank;
      $[1] = size;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    return t4;
  }
  const isWinner = isFinalWinner || drop?.winning_context;
  if (isWinner) {
    let t4;
    if ($[3] !== rank) {
      t4 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-2"><TrophyOnlyBadge rank={rank} /></div>;
      $[3] = rank;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  let t4;
  if ($[5] !== rank) {
    t4 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-2"><WinnerDropBadge rank={rank} decisionTime={null} /></div>;
    $[5] = rank;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};